import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createAqSession,
    deleteAqSession,
    moveAqSession,
    GOBLIN_COMBAT_POINTS,
    ORC_COMBAT_POINTS,
    LEVEL_CLEAR_BONUS_PER_LEVEL,
} from './adventureGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import CoopModePicker from './CoopModePicker';
import AdventureQuestCoop, { aqRoomIdFromLocation } from './AdventureQuestCoop';

const GAME_ID = 'adventure_quest';
const { UPLOAD_URL } = ARCADE.adventure_quest;
const ACCENT = GAME_ACCENT.adventure_quest;
const HINT = 'Arrows / WASD move · Esc pause · reach the exit (bottom-right)';

/** Internal canvas resolution — CSS scales down inside the play column. */
export const AQ_CANVAS_W = 960;
export const AQ_CANVAS_H = 640;

export const AQ_PLAYFIELD_SX = {
    p: 0.5,
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderRadius: 2,
    border: '2px solid rgba(255,215,0,0.3)',
    boxShadow: '0 0 20px rgba(255,215,0,0.2)',
    overflow: 'hidden',
};

export const AQ_CANVAS_STYLE = {
    border: '1px solid rgba(255,215,0,0.5)',
    borderRadius: '8px',
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: '100%',
    objectFit: 'contain',
    aspectRatio: `${AQ_CANVAS_W} / ${AQ_CANVAS_H}`,
};

export { GOBLIN_COMBAT_POINTS, ORC_COMBAT_POINTS, LEVEL_CLEAR_BONUS_PER_LEVEL };

const CELL = 32;
const GRID_W = 30;
const GRID_H = 20;

const paint = (canvas, data) => {
    if (!canvas || !data?.grid) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, AQ_CANVAS_W, AQ_CANVAS_H);

    const grid = data.grid;
    for (let y = 0; y < GRID_H; y++) {
        for (let x = 0; x < GRID_W; x++) {
            const wall = grid[y]?.[x] === 1;
            ctx.fillStyle = wall ? '#3d2c1e' : '#1a2332';
            ctx.fillRect(x * CELL, y * CELL, CELL - 1, CELL - 1);
        }
    }

    // Exit
    ctx.fillStyle = '#2ecc71';
    ctx.fillRect((GRID_W - 2) * CELL, (GRID_H - 2) * CELL, CELL - 1, CELL - 1);

    (data.treasures || []).forEach((t) => {
        ctx.fillStyle = t.type === 'gem' ? '#9b59b6' : '#f1c40f';
        ctx.beginPath();
        ctx.arc(t.x * CELL + CELL / 2, t.y * CELL + CELL / 2, 8, 0, Math.PI * 2);
        ctx.fill();
    });

    (data.enemies || []).forEach((e) => {
        ctx.fillStyle = e.type === 'orc' ? '#c0392b' : '#e67e22';
        ctx.fillRect(e.x * CELL + 4, e.y * CELL + 4, CELL - 8, CELL - 8);
    });

    const p = data.player || {};
    ctx.fillStyle = ACCENT;
    ctx.beginPath();
    ctx.arc((p.x || 1) * CELL + CELL / 2, (p.y || 1) * CELL + CELL / 2, 10, 0, Math.PI * 2);
    ctx.fill();
};

const AdventureQuestSolo = ({ headerContent }) => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const canvasRef = useRef(null);
    const [score, setScore] = useState(0);
    const [level, setLevel] = useState(1);
    const [health, setHealth] = useState(100);
    const [gold, setGold] = useState(0);
    const [gems, setGems] = useState(0);
    const [steps, setSteps] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [isVictory, setIsVictory] = useState(false);
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);
    const [gmSnapshot, setGmSnapshot] = useState(null);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        setGmSnapshot(data);
        if (typeof data.score === 'number') setScore(data.score);
        if (typeof data.level === 'number') setLevel(data.level);
        if (typeof data.step_count === 'number') setSteps(data.step_count);
        const player = data.player || {};
        if (typeof player.health === 'number') setHealth(player.health);
        if (typeof player.gold === 'number') setGold(player.gold);
        if (typeof player.gems === 'number') setGems(player.gems);
        setIsGameOver(Boolean(data.is_game_over));
        setIsVictory(Boolean(data.is_victory));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            victory: Boolean(data.is_victory),
            player,
        };
    }, [setMessage]);

    useEffect(() => {
        paint(canvasRef.current, gmSnapshot);
    }, [gmSnapshot]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteAqSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createAqSession();
        if (!data?.session_id) throw new Error('Adventure Quest GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        recorder.reset({ init_state: data.init_state || { x: 1, y: 1, level: 1 } });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteAqSession(sid);
        play.endGame({ score });
        setGmSnapshot(null);
        setScore(0);
        setLevel(1);
        setHealth(100);
        setGold(0);
        setGems(0);
        setSteps(0);
        setIsGameOver(false);
        setIsVictory(false);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteAqSession(sid);
    }, []);

    const step = useCallback(async (action) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveAqSession(sid, action);
            const meta = applyGm(data);
            // Step responses carry only the newest history entry (last_event).
            const last = data.last_event
                || (Array.isArray(data.history) ? data.history[data.history.length - 1] : null);
            recorder.recordTick({
                action,
                x: last?.x ?? meta.player?.x,
                y: last?.y ?? meta.player?.y,
                score: meta.score,
                health: meta.player?.health,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: meta.score,
                    extra: {
                        victory: meta.victory,
                        level: data.level,
                        gold: meta.player?.gold,
                        gems: meta.player?.gems,
                    },
                });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteAqSession(done);
            }
        } catch (err) {
            console.warn('Adventure Quest step failed', err);
            setMessage(err?.data?.message || 'Move failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isGameOver, play, recorder, setMessage]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) { step('up'); e.preventDefault(); }
        else if (['ArrowDown', 's', 'S'].includes(e.key)) { step('down'); e.preventDefault(); }
        else if (['ArrowLeft', 'a', 'A'].includes(e.key)) { step('left'); e.preventDefault(); }
        else if (['ArrowRight', 'd', 'D'].includes(e.key)) { step('right'); e.preventDefault(); }
    }, [step]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [step],
    });

    return (
        <ArcadeGameShell
            title="Adventure"
            titleAccent="Quest"
            subtitle="Explore the dungeon — collect treasure, reach the exit"
            accentColor={ACCENT}
            headerContent={headerContent}
            boilerplate={GAME_BOILERPLATE.adventure_quest}
            session={session}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip label={`Score: ${score}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`HP: ${health}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Gold: ${gold}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Gems: ${gems}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Steps: ${steps}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Lv ${level}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip
                            label={isVictory ? 'Victory' : isGameOver ? 'Defeated' : 'Exploring'}
                            size="small"
                            color={isVictory ? 'success' : isGameOver ? 'error' : 'warning'}
                        />
                    </Stack>
                    {message && (
                        <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>
                    )}
                    <Box sx={AQ_PLAYFIELD_SX}>
                        <canvas
                            ref={canvasRef}
                            width={AQ_CANVAS_W}
                            height={AQ_CANVAS_H}
                            style={AQ_CANVAS_STYLE}
                        />
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

/**
 * Adventure Quest page: Solo (GM session, unchanged) or Online Co-op for 2–4
 * players (gateway room, AdventureQuestCoop). ?aq_room=<id> opens Co-op.
 */
const AdventureQuest = () => {
    const [mode, setMode] = useState(() => (aqRoomIdFromLocation() ? 'coop' : 'solo'));
    const [coopLive, setCoopLive] = useState(false);
    const picker = (
        <CoopModePicker value={mode} onChange={setMode} accentColor={ACCENT} disabled={coopLive} testId="aq-mode-picker" />
    );
    if (mode === 'coop') return <AdventureQuestCoop headerContent={picker} onLiveChange={setCoopLive} />;
    return <AdventureQuestSolo headerContent={picker} />;
};

export default AdventureQuest;
